import {
  Component, Input, OnDestroy, OnInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfigurationService, IDirectoryAppConfig } from '@sondermind/configuration';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { Subject, take, takeUntil } from 'rxjs';
import { INSURANCE_PRIORITY_LIST } from '../../models/insurance-priority-list';

/**
 * This footer is used in public-facing applications like the
 * provider-landing-page and provider-directory.
 */
@Component({
  selector: 'public-footer',
  templateUrl: './public-footer.component.html',
  styleUrls: ['./public-footer.component.scss']
})
export class PublicFooterComponent implements OnInit, OnDestroy {
  emergencyResources = InfoLinks.EMERGENCY_RESOURCES;
  destroyed$ = new Subject<void>();

  @Input() matchflowBaseUrl: string = '';
  @Input() set currentStateAbbrev(currState: string) {
    this.currStateAbbrev = currState;
    if (currState) {
      this.checkInsuranceFooterOptions();
    }
  }

  currStateAbbrev: string = '';
  therapyMatchFlowUrl: string = '';
  psychiatryMatchFlowUrl: string = '';
  providerDirectoryUrl: string = '';
  stateInsurances: string [] = [];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MAX_INSURANCES: number = 5;
  private anonUserId: string = '';

  constructor(
    private route: ActivatedRoute,
    private configurationService: ConfigurationService<IDirectoryAppConfig>,
    private insuranceStore: ProviderNetworkCompanyStore
  ) {}

  ngOnInit(): void {
    const referringUrl = encodeURIComponent(window.location.href);
    this.therapyMatchFlowUrl = `${this.matchflowBaseUrl}?referringUrl=${referringUrl}`;
    this.psychiatryMatchFlowUrl = `${this.matchflowBaseUrl}/?slug=psychiatry-flow&referringUrl=${referringUrl}`;
    this.providerDirectoryUrl = this.configurationService.env.providerDirectoryBase
                                || 'https://www.sondermind.com/provider-directory';

    this.route.queryParams.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((queryParams) => {
      if (queryParams['anonUserId']) {
        this.anonUserId = queryParams['anonUserId'] as string;
        this.therapyMatchFlowUrl = `${this.therapyMatchFlowUrl}&anonUserId=${this.anonUserId}`;
      }
    });
  }

  checkInsuranceFooterOptions() {
    this.insuranceStore.getInsuranceOptionsByState$(this.currStateAbbrev).pipe(take(1)).subscribe((list) => {
      const insuranceTitleMap: { [key: string]: string; } = {};
      const insuranceNameMap: { [key: string]: string; } = {};
      const medicareTitleMap: { [key: string]: string; } = {};
      const medicareNameMap: { [key: string]: string; } = {};
      // Combine both lists and map insurance names to their titles for quick lookup
      list.forEach((insuranceObj) => {
        if (!insuranceObj.isMedicare) {
          insuranceTitleMap[insuranceObj.title] = insuranceObj.title;
          insuranceNameMap[insuranceObj.insuranceName] = insuranceObj.title;
        } else {
          medicareTitleMap[insuranceObj.title] = insuranceObj.title;
          medicareNameMap[insuranceObj.insuranceName] = insuranceObj.title;
        }
      });
      // Check against priority list
      this.stateInsurances = INSURANCE_PRIORITY_LIST
        .map((insuranceName) => insuranceTitleMap[insuranceName]
                                || medicareTitleMap[insuranceName]
                                || insuranceNameMap[insuranceName]
                                || medicareNameMap[insuranceName])
        .filter(Boolean)
        .slice(0, this.MAX_INSURANCES);
    });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
